import * as fhirpath from 'fhirpath';
import React from 'react';
import type { DropdownProps } from 'semantic-ui-react';
import { Form, Loader } from 'semantic-ui-react';

/**
 * One `Practitioner` entry as it arrives in a FHIR search bundle.
 *
 * `resource` is NOT `fhir/r4`'s `Practitioner`: that narrows `resourceType` to a
 * literal, which the spec's plain-object fixtures do not satisfy, and one of
 * them omits `name` to pin the no-name render. Only `id` and `name` are read
 * here, both through `fhirpath.evaluate`.
 */
type PractitionerEntry = {
  resource: {
    id?: string;
    name?: unknown;
    resourceType?: string;
  };
};

/**
 * What the practitioner picker accepts. Call sites are in
 * `components/patient/careplan/steps/careteam/index.tsx`.
 *
 * `PractitionerData` is optional because absence is the loader branch.
 *
 * `onSelect` takes `string | null`: `selectedPractitioner` starts as `null` and
 * `handleSubmit` does not guard it, so submitting without touching the dropdown
 * hands `null` through. Existing behaviour, pinned in `practitioners.spec.tsx`.
 */
type PractitionersProps = {
  PractitionerData?: PractitionerEntry[] | undefined;
  /** Receives `'<id>|<First> <Last>'`, or `null` when submitted untouched. */
  onSelect: (selected: string | null) => void;
  /**
   * Inert: both care-team pickers pass these and nothing here reads them, so the
   * intended role filter never happens — SUSPECTED DEFECT at those call sites.
   */
  org?: string | undefined;
  type?: string | undefined;
};

function Practitioners(props: PractitionersProps) {
  const [selectedPractitioner, setSelectedPractitioner] = React.useState<string | null>(null);

  const handleSelect = (e: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    // Single-select over string-valued options built below, so `value` is a
    // string here even though `DropdownProps` admits arrays and booleans.
    setSelectedPractitioner(value as string);
  };

  const handleSubmit = (_e: React.FormEvent<HTMLFormElement>) => {
    props.onSelect(selectedPractitioner);
  };

  return (
    <>
      {!props.PractitionerData && <Loader active inline="centered" />}
      {props.PractitionerData && (
        <Form onSubmit={handleSubmit}>
          <Form.Group>
            <Form.Dropdown
              placeholder="Select Practitioner"
              search
              selection
              selectOnBlur={false}
              options={props.PractitionerData?.map((practitionerEntry: PractitionerEntry) => {
                const practitioner = practitionerEntry.resource;
                // Both are fhirpath COLLECTIONS (arrays), concatenated with `+`
                // below and so coerced via `Array.prototype.toString`. A
                // practitioner with two family names renders 'Rivera,Smith' —
                // pinned by practitioners.spec.tsx, which is why they are left as
                // arrays rather than indexed to their first element here.
                const firstName = fhirpath.evaluate(practitioner, 'name.given.first()');
                const lastName = fhirpath.evaluate(practitioner, 'name.family');
                return {
                  key: practitioner.id,
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  text: firstName + ' ' + lastName,
                  // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                  value: practitioner.id + '|' + firstName + ' ' + lastName,
                };
              })}
              onChange={handleSelect}
            />
            <Form.Button primary content="Select" />
          </Form.Group>
        </Form>
      )}
    </>
  );
}

export default Practitioners;
